<script setup>
import { ref, onMounted, computed } from 'vue';
import schedulingAPI from '../api/scheduling';

const emit = defineEmits(['close']);

const settings = ref({
  public_slug: '',
  company_name: '',
  company_description: '',
  business_hours: {
    monday: { enabled: true, start: '08:00', end: '18:00' },
    tuesday: { enabled: true, start: '08:00', end: '18:00' },
    wednesday: { enabled: true, start: '08:00', end: '18:00' },
    thursday: { enabled: true, start: '08:00', end: '18:00' },
    friday: { enabled: true, start: '08:00', end: '18:00' },
    saturday: { enabled: false, start: '08:00', end: '12:00' },
    sunday: { enabled: false, start: '08:00', end: '12:00' },
  },
  webhook_enabled: false,
  webhook_url: '',
  webhook_events: ['appointment_created', 'appointment_confirmed', 'appointment_cancelled', 'appointment_completed'],
  notion_enabled: false,
  notion_api_key: '',
  notion_database_id: '',
});

const activeSection = ref('business'); // 'business', 'link', 'ai_webhook', 'notion'
const isLoading = ref(false);
const isSaving = ref(false);
const isTestingWebhook = ref(false);
const isTestingNotion = ref(false);
const webhookTestResult = ref(null);
const notionTestResult = ref(null);
const linkCopied = ref(false);

const weekDayKeys = [
  { key: 'monday', label: 'Segunda-feira' },
  { key: 'tuesday', label: 'Terça-feira' },
  { key: 'wednesday', label: 'Quarta-feira' },
  { key: 'thursday', label: 'Quinta-feira' },
  { key: 'friday', label: 'Sexta-feira' },
  { key: 'saturday', label: 'Sábado' },
  { key: 'sunday', label: 'Domingo' },
];

const availableEvents = [
  { key: 'appointment_created', label: 'Novo Agendamento Criado' },
  { key: 'appointment_confirmed', label: 'Agendamento Confirmado' },
  { key: 'appointment_cancelled', label: 'Agendamento Cancelado' },
  { key: 'appointment_completed', label: 'Atendimento Concluído' },
];

const publicBookingUrl = computed(() => {
  const slug = settings.value.public_slug || 'empresa';
  return `${window.location.origin}/schedule/${slug}`;
});

const copyPublicLink = async () => {
  try {
    await navigator.clipboard.writeText(publicBookingUrl.value);
    linkCopied.value = true;
    setTimeout(() => {
      linkCopied.value = false;
    }, 2500);
  } catch (err) {
    console.error('Falha ao copiar link:', err);
  }
};

const openPublicLink = () => {
  window.open(publicBookingUrl.value, '_blank');
};

const fetchSettings = async () => {
  isLoading.value = true;
  try {
    const res = await schedulingAPI.getSettings();
    if (res.data) {
      settings.value = {
        ...settings.value,
        ...res.data,
      };
    }
  } catch (err) {
    console.error('Erro ao carregar configurações:', err);
  } finally {
    isLoading.value = false;
  }
};

const saveSettings = async () => {
  isSaving.value = true;
  try {
    await schedulingAPI.updateSettings(settings.value);
    alert('Configurações salvas com sucesso!');
    emit('close');
  } catch (err) {
    alert('Erro ao salvar configurações.');
  } finally {
    isSaving.value = false;
  }
};

const testWebhook = async () => {
  if (!settings.value.webhook_url) {
    alert('Informe a URL do webhook.');
    return;
  }

  isTestingWebhook.value = true;
  webhookTestResult.value = null;
  try {
    const res = await schedulingAPI.testWebhook({ webhook_url: settings.value.webhook_url });
    webhookTestResult.value = res.data;
  } catch (err) {
    webhookTestResult.value = err.response?.data || { success: false, message: err.message };
  } finally {
    isTestingWebhook.value = false;
  }
};

const testNotion = async () => {
  if (!settings.value.notion_api_key || !settings.value.notion_database_id) {
    alert('Informe a Chave de API e o ID do Database do Notion.');
    return;
  }

  isTestingNotion.value = true;
  notionTestResult.value = null;
  try {
    const res = await schedulingAPI.testNotion({
      notion_api_key: settings.value.notion_api_key,
      notion_database_id: settings.value.notion_database_id,
    });
    notionTestResult.value = res.data;
  } catch (err) {
    notionTestResult.value = err.response?.data || { success: false, message: err.message };
  } finally {
    isTestingNotion.value = false;
  }
};

const toggleEvent = key => {
  const list = settings.value.webhook_events || [];
  const idx = list.indexOf(key);
  if (idx > -1) {
    list.splice(idx, 1);
  } else {
    list.push(key);
  }
};

onMounted(() => {
  fetchSettings();
});
</script>

<template>
  <div
    class="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 dark:bg-slate-950/80 backdrop-blur-xs animate-fade-in select-none"
    @click.self="$emit('close')"
  >
    <div
      class="w-full max-w-3xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-2xl overflow-hidden text-slate-800 dark:text-slate-100 flex flex-col max-h-[90vh]"
    >
      <!-- Header -->
      <div class="px-6 py-4 border-b border-slate-200 dark:border-slate-800 flex items-center justify-between bg-slate-50 dark:bg-slate-950/60">
        <div>
          <h3 class="text-sm font-bold text-slate-900 dark:text-white flex items-center gap-2">
            <span>⚙️</span> Configurações de Atendimento & Agendamentos
          </h3>
          <p class="text-xs text-slate-500 dark:text-slate-400">
            Horários da empresa, link público de agendamento, IA e integrações.
          </p>
        </div>
        <button type="button" class="text-slate-400 hover:text-slate-700 dark:hover:text-white cursor-pointer" @click="$emit('close')">✕</button>
      </div>

      <!-- Navigation Tabs Inside Modal -->
      <div class="px-6 pt-3 border-b border-slate-200 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/40 flex items-center gap-2 overflow-x-auto">
        <button
          type="button"
          class="px-3.5 py-2 text-xs font-bold transition-all border-b-2 cursor-pointer shrink-0"
          :class="activeSection === 'business' ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'"
          @click="activeSection = 'business'"
        >
          ⏰ Funcionamento da Empresa
        </button>

        <button
          type="button"
          class="px-3.5 py-2 text-xs font-bold transition-all border-b-2 cursor-pointer shrink-0"
          :class="activeSection === 'link' ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'"
          @click="activeSection = 'link'"
        >
          🔗 Link Público
        </button>

        <button
          type="button"
          class="px-3.5 py-2 text-xs font-bold transition-all border-b-2 cursor-pointer shrink-0"
          :class="activeSection === 'ai_webhook' ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'"
          @click="activeSection = 'ai_webhook'"
        >
          🤖 Webhook para IA
        </button>

        <button
          type="button"
          class="px-3.5 py-2 text-xs font-bold transition-all border-b-2 cursor-pointer shrink-0"
          :class="activeSection === 'notion' ? 'border-blue-600 dark:border-blue-500 text-blue-600 dark:text-blue-400' : 'border-transparent text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200'"
          @click="activeSection = 'notion'"
        >
          📝 Notion Database
        </button>
      </div>

      <!-- Content Area -->
      <div class="p-6 overflow-y-auto space-y-5 flex-1">
        <!-- 1. Funcionamento da Empresa -->
        <div v-if="activeSection === 'business'" class="space-y-4">
          <div class="p-3 bg-blue-50 dark:bg-blue-600/10 border border-blue-200 dark:border-blue-500/20 rounded-xl text-xs text-blue-700 dark:text-blue-300">
            Defina aqui os dias e horários em que o estabelecimento está aberto para receber agendamentos. A agenda respeitará esta janela geral.
          </div>

          <div class="space-y-2">
            <div
              v-for="day in weekDayKeys"
              :key="day.key"
              class="flex items-center justify-between p-2.5 rounded-xl bg-slate-50 dark:bg-slate-950 border border-slate-200 dark:border-slate-800 text-xs"
            >
              <label class="flex items-center gap-2.5 font-medium cursor-pointer text-slate-800 dark:text-slate-200">
                <input
                  v-model="settings.business_hours[day.key].enabled"
                  type="checkbox"
                  class="rounded text-blue-600 focus:ring-blue-500 dark:bg-slate-900 dark:border-slate-700"
                />
                <span>{{ day.label }}</span>
              </label>

              <div v-if="settings.business_hours[day.key].enabled" class="flex items-center gap-2">
                <span class="text-slate-500 dark:text-slate-400 text-[11px]">Das</span>
                <input
                  v-model="settings.business_hours[day.key].start"
                  type="time"
                  class="h-7 px-2 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
                <span class="text-slate-500 dark:text-slate-400 text-[11px]">às</span>
                <input
                  v-model="settings.business_hours[day.key].end"
                  type="time"
                  class="h-7 px-2 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-lg text-slate-900 dark:text-white focus:outline-none focus:ring-1 focus:ring-blue-500"
                />
              </div>
              <span v-else class="text-slate-400 dark:text-slate-500 text-[11px] font-medium">Fechado</span>
            </div>
          </div>
        </div>

        <!-- 2. Link Público -->
        <div v-else-if="activeSection === 'link'" class="space-y-4">
          <div class="p-4 rounded-2xl bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-950/60 dark:to-indigo-950/60 border border-blue-200 dark:border-blue-800/60 space-y-3">
            <div class="flex items-start justify-between">
              <div>
                <h4 class="text-xs font-bold text-blue-950 dark:text-white flex items-center gap-1.5">
                  <span>📅</span> Seu Link Público de Agendamento
                </h4>
                <p class="text-[11px] text-slate-600 dark:text-slate-300">
                  Envie este link para clientes no WhatsApp ou insira no seu site/Instagram para agendamentos online.
                </p>
              </div>
              <button
                type="button"
                class="px-2.5 py-1 text-[11px] font-bold bg-blue-100 hover:bg-blue-200 text-blue-700 dark:bg-blue-600/30 dark:text-blue-300 dark:hover:bg-blue-600/50 rounded-lg border border-blue-300 dark:border-blue-500/40 transition-colors cursor-pointer"
                @click="openPublicLink"
              >
                ↗️ Abrir Página
              </button>
            </div>

            <div class="flex items-center gap-2 p-2 rounded-xl bg-white dark:bg-slate-950/90 border border-slate-200 dark:border-slate-800">
              <input
                :value="publicBookingUrl"
                readonly
                class="flex-1 bg-transparent border-0 text-xs font-mono text-blue-700 dark:text-blue-300 focus:outline-none select-all"
              />
              <button
                type="button"
                class="px-3.5 py-1.5 rounded-lg text-xs font-bold transition-all flex items-center gap-1 cursor-pointer"
                :class="linkCopied ? 'bg-emerald-600 text-white' : 'bg-blue-600 hover:bg-blue-500 text-white'"
                @click="copyPublicLink"
              >
                <span>{{ linkCopied ? '✓ Copiado!' : 'Copiar Link' }}</span>
              </button>
            </div>
          </div>

          <div class="grid grid-cols-2 gap-3 pt-2">
            <div>
              <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Nome de Exibição na Página
              </label>
              <input
                v-model="settings.company_name"
                type="text"
                placeholder="Ex: Clínica Saúde & Vida"
                class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-slate-800 text-slate-900 dark:text-white"
              />
            </div>
            <div>
              <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
                Identificador na URL (Slug)
              </label>
              <input
                v-model="settings.public_slug"
                type="text"
                placeholder="clinica-saude"
                class="w-full h-9 px-3 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-slate-800 text-slate-900 dark:text-white font-mono"
              />
            </div>
          </div>

          <div>
            <label class="block text-xs font-semibold text-slate-700 dark:text-slate-300 mb-1">
              Descrição / Mensagem de Boas-Vindas aos Clientes
            </label>
            <textarea
              v-model="settings.company_description"
              rows="2"
              placeholder="Escolha abaixo o serviço e o melhor horário para o seu atendimento..."
              class="w-full p-2.5 text-xs bg-slate-50 dark:bg-slate-800 border border-slate-300 dark:border-slate-700 rounded-xl focus:outline-none focus:border-blue-500 focus:bg-white dark:focus:bg-slate-800 text-slate-900 dark:text-white resize-none"
            />
          </div>
        </div>

        <!-- 3. Webhook para Atendimento de IA -->
        <div v-else-if="activeSection === 'ai_webhook'" class="space-y-4">
          <div class="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 space-y-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-blue-500" />
                <h4 class="text-xs font-bold text-slate-900 dark:text-white">Webhook para IA (Typebot / n8n / Agentes)</h4>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input v-model="settings.webhook_enabled" type="checkbox" class="sr-only peer" />
                <div class="w-9 h-5 bg-slate-300 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-blue-600"></div>
              </label>
            </div>

            <div v-if="settings.webhook_enabled" class="space-y-3 pt-2 border-t border-slate-200 dark:border-slate-800">
              <div>
                <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  URL do Webhook (Endpoint de Destino)
                </label>
                <div class="flex items-center gap-2">
                  <input
                    v-model="settings.webhook_url"
                    type="url"
                    placeholder="https://seu-n8n.com/webhook/agendamentos"
                    class="flex-1 h-9 px-3 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl focus:outline-none focus:border-blue-500 text-slate-900 dark:text-white"
                  />
                  <button
                    type="button"
                    class="px-3 py-2 bg-blue-50 hover:bg-blue-100 dark:bg-blue-600/20 dark:hover:bg-blue-600/30 text-blue-600 dark:text-blue-400 text-xs font-bold rounded-xl border border-blue-200 dark:border-blue-500/40 transition-colors cursor-pointer shrink-0"
                    :disabled="isTestingWebhook"
                    @click="testWebhook"
                  >
                    {{ isTestingWebhook ? 'Testando...' : '⚡ Testar Webhook' }}
                  </button>
                </div>
              </div>

              <div v-if="webhookTestResult" class="p-2.5 rounded-xl text-xs" :class="webhookTestResult.success ? 'bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-400'">
                {{ webhookTestResult.message }}
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1.5">
                  Eventos Disparados
                </label>
                <div class="grid grid-cols-2 gap-2">
                  <label
                    v-for="ev in availableEvents"
                    :key="ev.key"
                    class="flex items-center gap-2 text-xs text-slate-700 dark:text-slate-300 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      :checked="settings.webhook_events.includes(ev.key)"
                      class="rounded text-blue-600 focus:ring-blue-500 dark:bg-slate-900 dark:border-slate-700"
                      @change="toggleEvent(ev.key)"
                    />
                    <span>{{ ev.label }}</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- 4. Integração Nativa com Notion -->
        <div v-else-if="activeSection === 'notion'" class="space-y-4">
          <div class="p-4 rounded-2xl bg-slate-50 dark:bg-slate-950/80 border border-slate-200 dark:border-slate-800 space-y-4">
            <div class="flex items-center justify-between">
              <div class="flex items-center gap-2">
                <span class="w-2.5 h-2.5 rounded-full bg-purple-500" />
                <h4 class="text-xs font-bold text-slate-900 dark:text-white">Sincronização Nativa com Notion</h4>
              </div>
              <label class="relative inline-flex items-center cursor-pointer">
                <input v-model="settings.notion_enabled" type="checkbox" class="sr-only peer" />
                <div class="w-9 h-5 bg-slate-300 dark:bg-slate-700 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-purple-600"></div>
              </label>
            </div>

            <div v-if="settings.notion_enabled" class="space-y-3 pt-2 border-t border-slate-200 dark:border-slate-800">
              <div>
                <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Notion Internal Integration Token (API Key)
                </label>
                <input
                  v-model="settings.notion_api_key"
                  type="password"
                  placeholder="secret_xxxxxxxxxxxxxxxxxxxxxxxxxxxx"
                  class="w-full h-9 px-3 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl focus:outline-none focus:border-purple-500 text-slate-900 dark:text-white font-mono"
                />
              </div>

              <div>
                <label class="block text-[11px] font-semibold text-slate-700 dark:text-slate-300 mb-1">
                  Notion Database ID
                </label>
                <div class="flex items-center gap-2">
                  <input
                    v-model="settings.notion_database_id"
                    type="text"
                    placeholder="32 caracteres hexadecimais do seu Database"
                    class="flex-1 h-9 px-3 text-xs bg-white dark:bg-slate-900 border border-slate-300 dark:border-slate-700 rounded-xl focus:outline-none focus:border-purple-500 text-slate-900 dark:text-white font-mono"
                  />
                  <button
                    type="button"
                    class="px-3 py-2 bg-purple-50 hover:bg-purple-100 dark:bg-purple-600/20 dark:hover:bg-purple-600/30 text-purple-600 dark:text-purple-400 text-xs font-bold rounded-xl border border-purple-200 dark:border-purple-500/40 transition-colors cursor-pointer shrink-0"
                    :disabled="isTestingNotion"
                    @click="testNotion"
                  >
                    {{ isTestingNotion ? 'Testando...' : '🔍 Validar Notion' }}
                  </button>
                </div>
              </div>

              <div v-if="notionTestResult" class="p-2.5 rounded-xl text-xs" :class="notionTestResult.success ? 'bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/30 text-emerald-700 dark:text-emerald-400' : 'bg-red-50 dark:bg-red-500/10 border border-red-200 dark:border-red-500/30 text-red-700 dark:text-red-400'">
                {{ notionTestResult.message }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="px-6 py-3.5 border-t border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-950 flex items-center justify-end gap-2.5">
        <button type="button" class="px-4 py-2 text-xs font-medium text-slate-600 hover:text-slate-900 dark:text-slate-400 dark:hover:text-white cursor-pointer" @click="$emit('close')">
          Cancelar
        </button>
        <button
          type="button"
          class="px-5 py-2 text-xs font-bold text-white bg-blue-600 hover:bg-blue-500 rounded-xl shadow-xs cursor-pointer disabled:opacity-50"
          :disabled="isSaving"
          @click="saveSettings"
        >
          {{ isSaving ? 'Salvando...' : 'Salvar Configurações' }}
        </button>
      </div>
    </div>
  </div>
</template>
